import { QuickAdd } from "../Main/QuickAdd";
import { BasketCommon } from "../Main/basketCommon";
import { ILinkedProductModel } from "./Interfaces/ILinkedProductModel";
import { IProductRecommendations } from "./Interfaces/IProductRecommendations";
import { ClickableSelector } from "./SelectorUI/ClickableSelector";
import { IColourSizeSelector } from "./SelectorUI/IColourSizeSelector";
import { IProductPageStockModel } from "./Interfaces/IProductPageStock";
import { IProductPage } from "./Interfaces/IProductPage";
import { StockLevel } from "./StockLevel";
import { ProductClass } from "./ProductClass";

// Functions and vars output on the page and from non TypeScript libs.
declare var stockInfo: { [key: string]: IProductPageStockModel };
declare var Colours: { [key: string]: string };
declare var Sizes: { [key: string]: string };

interface ISlideCaptionAndIndex {
    caption: string;
    index: number;
    isNewColour: boolean;
    isVideo: boolean;
}

export class Product implements IProductPage {
    private currentThumbnailIndex: number = 0;
    private deliveryTimerHander: number = 0;
    private productClass: ProductClass = ProductClass.PhysicalProduct;
    private styleId: number = 0;
    private priceElement: HTMLElement;
    private priceExVatElement: HTMLElement;
    private percentageOffElement: HTMLElement;
    private selectorUi: IColourSizeSelector;
    private lastRequestTime: number;
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;

    private readonly addToBasketButton = document.getElementById("AddToBasket") as HTMLButtonElement;
    private readonly addToWishlistButton = document.getElementById("AddToWishListButton") as HTMLButtonElement;
    private readonly basketCommon: BasketCommon = new BasketCommon();
    // Optional on some tenants, always check for existence before use.
    private readonly checkStoreStockElement = document.getElementById("check-store-stock");

    private readonly desktopZoom1Id = "productzoom-1";
    private readonly desktopZoom2Id = "productzoom-2";
    private readonly desktopVideoId = "video-left";
    private readonly mobileZoomId = "productmobilezoom";
    private readonly mobileVideoId = "videomobilezoom";

    private readonly desktopTheLookContainerId = "TheLookResults";
    private readonly mobileTheLookContainerId = "TheLookMobile";
    private readonly desktopTheLookMagicScrollId = "the-look-ms";
    private readonly mobileTheLookMagicScrollId = "the-look-ms-mobile";

    private readonly magicZoomThumbSelectedClass = "mz-thumb-selected";
    public readonly quickAdd: QuickAdd;

    public static Init(): Product {
        return new Product();
    }

    constructor() {

        this.configureMagicZoom();

        this.priceElement = document.getElementById('item-price');
        this.priceExVatElement = document.getElementById('PriceWithoutVAT');
        this.percentageOffElement = document.getElementById('PercentageOff');
        this.productClass = ProductClass.PhysicalProduct;

        let styleIdElement = document.getElementById("StyleId") as HTMLInputElement;
        if (styleIdElement) { // Styleid and product class elements are not output if no stock.
            this.styleId = parseInt(styleIdElement.value);
            this.productClass = parseInt((<HTMLInputElement>document.getElementById("ProductClass")).value);

            if (this.productClass === ProductClass.GiftCard) {
                this.basketCommon.WireUpPriceEditMonitoring(() => this.onGiftCardPriceChanged());
            }
        }

        this.UpdateDelivery(this.selectedProductId, false);

        if (this.slideCount() <= 1) {
            this.HideDesktopImagePaging();
        }

        this.selectorUi = new ClickableSelector(Colours, Sizes, this);

        this.WireUpImagePaging();
        this.WireUpThumbnailClicks();
        this.wireUpStoreStock();
        this.WireUpBazaar();
        this.WireUpAddToBasket();
        this.WireUpAddToWishlist();
        this.WireUpStoreStockCheck();
        this.quickAdd = new QuickAdd();
    }

    private configureMagicZoom(): void {

        (window as any).mzOptions = {
            onZoomReady: function () {
            },
            onUpdate: (zoom_id, previous_image_selector, new_image_selector) => {
                // We have two MagicScroll / MagicZooms (left and right) and this ensures the 2nd tracks the first.
                this.SetCurrentThumbIndex(zoom_id, new_image_selector);
            },
            onZoomIn: function () {
            },
            onZoomOut: function () {
            }
        };
    }

    public SetCurrentThumbIndex(zoom_id: string, new_image_selector: HTMLAnchorElement): void {
        if (zoom_id === this.desktopZoom1Id) {
            let slideIndex = parseInt(new_image_selector.dataset["imageIndex"]);
            this.currentThumbnailIndex = slideIndex;
            this.updateZoomWindows();
        }
        else if (zoom_id === this.mobileZoomId) {
            let slideInfo = this.slideInfoFromLink(new_image_selector);
            this.updateZoomCaption("3", slideInfo.caption);
        }
    }

    private HideDesktopImagePaging() {
        let previousImage = document.getElementById("c-previous-image");
        let nextImage = document.getElementById("c-next-image");

        if (previousImage) {
            previousImage.classList.add("hidden");
        }
        if (nextImage) {
            nextImage.classList.add("hidden");
        }
    }

    private WireUpBazaar(): void {
        // bvCallback must be used when interacting with BV via JavaScript and using the `async` attribute. 
        (<any>window).bvCallback = (BV) => this.bazaarCallback(BV);
    }

    private bazaarCallback(BV: any): void {

        if (!BV) {
            return;
        }

        if (BV.reviews) {
            // Register a function to be called when a BV feature needs to display the R&R element, such as Rating Summary's stars
            BV.reviews.on('show', () => {
                let tabLink = document.querySelector<HTMLInputElement>("BazaarReviewsTab");
                tabLink.click();
            });
        }

        if (BV.questions) {
            // Register a function to be called when a BV feature needs to 
            // display the Q&A element, such as when clicking on the number of questions link
            BV.questions.on('show', function () {
                let tabLink = <HTMLInputElement>document.getElementById("BazaarQAndATab");
                tabLink.click();
            });
        }
    }

    private WireUpAddToBasket(): void {
        this.addToBasketButton.addEventListener("click", (e) => { this.onAddToBasketClicked(e); return false; });
    }

    private WireUpStoreStockCheck(): void {
        if (this.checkStoreStockElement) {
            this.checkStoreStockElement.addEventListener("click", (e) => { this.onStoreStockCheckClicked(e); return false; });
        }
    }

    private WireUpAddToWishlist(): void {
        this.addToWishlistButton.addEventListener("click", (e) => { this.onAddToWishlistClicked(e); return false; });
    }

    private onGiftCardPriceChanged(): void {
        let price = this.basketCommon.giftCardValue();

        if (price == null) {
            this.SetButtonState(StockLevel.Stock_NotSelected);
            return;
        }

        this.SetButtonState(StockLevel.Stock_InStock);
    }

    private WireUpThumbnailClicks(): void {
        // zoom-1-thumb is the class applied to all the thumbnails (video and static images) that show on the left hand pane for desktop (not mobile).
        let elements = <HTMLCollectionOf<HTMLElement>>document.getElementsByClassName("zoom-1-thumb");

        for (const element of elements) {
            element.addEventListener("click", (e) => this.onDesktopMagicScrollThumbClicked(e));
        }
    }

    private onDesktopMagicScrollThumbClicked(e: MouseEvent): boolean {
        e.preventDefault();
        let thumbClickedElement = <HTMLAnchorElement>e.target;

        this.setPaneVisibility(thumbClickedElement, this.desktopZoom1Id, this.desktopVideoId);
        this.triggerSlideTwoUpdate(thumbClickedElement);

        return false;
    }

    private setPaneVisibility(thumbClickedElement: HTMLAnchorElement, imageZoomId: string, videoDivId: string): void {
        let isVideo = thumbClickedElement.dataset["isVideo"];
        let imageZoom = document.getElementById(imageZoomId);
        let videoDiv = document.getElementById(videoDivId);

        if (isVideo && isVideo.toLowerCase() === "true") {
            let mediaUrl = thumbClickedElement.dataset["image"];
            this.recreateVideo(videoDiv, mediaUrl);

            // Remove all of the 'selected' CSS classes from the MagicScrolls as MagicX does not handle video, we have to do it.
            let allSelectedThumbs = <HTMLCollectionOf<HTMLInputElement>>document.getElementsByClassName(this.magicZoomThumbSelectedClass);
            Product.removeClassFromElements(allSelectedThumbs, this.magicZoomThumbSelectedClass);
            // Mark our video anchor as active / selected.
            thumbClickedElement.classList.add(this.magicZoomThumbSelectedClass);

            if (imageZoom) {
                imageZoom.classList.add("hidden");
            }
            if (videoDiv) {
                videoDiv.classList.remove("hidden");
            }

            return;
        }

        // Again, MagicX does not handle video so we need to deselect the active element on the videos when an image is clicked.
        let selectedNodes = document.querySelectorAll(`a.${this.magicZoomThumbSelectedClass}[data-is-video='True']`) as any;
        let selectedVideoThumbs = <HTMLCollectionOf<HTMLInputElement>>selectedNodes;
        Product.removeClassFromElements(selectedVideoThumbs, this.magicZoomThumbSelectedClass);

        // When the click has come from MagicScroll, it will handle updating the current image so we don't set it here.
        if (imageZoom) {
            imageZoom.classList.remove('hidden');
        }
        if (videoDiv) {
            videoDiv.classList.add('hidden');
        }
    }

    private static removeClassFromElements(elements: HTMLCollectionOf<HTMLElement>, cls: string): void {
        for (const element of elements) {
            element.classList.remove(cls);
        }
    }

    private recreateVideo(containingDiv: HTMLElement, mediaUrl: string): void {
        let videoElement = document.createElement('video');

        containingDiv.innerHTML = "";

        videoElement.width = 402;
        videoElement.height = 482;
        videoElement.muted = true;
        videoElement.autoplay = true;
        videoElement.controls = true;
        videoElement.loop = false;

        let sourceMP4 = document.createElement("source");
        sourceMP4.type = "video/mp4";
        sourceMP4.src = mediaUrl;

        videoElement.appendChild(sourceMP4);
        containingDiv.appendChild(videoElement);
    }

    /**
     * Listens on the body class for clicks on a store stock popup.
     * The store stock popup is only loaded when clicked.
     */
    private wireUpStoreStock(): void {
        document.body.addEventListener('click', (e: MouseEvent) => {
            const blockHeader = (e.target as HTMLElement).closest('.blockHeader');

            if (blockHeader) {
                e.preventDefault();

                const blockContents = blockHeader.nextElementSibling as HTMLElement;

                if (blockHeader.classList.contains('active')) {
                    blockHeader.classList.remove('active');
                    blockContents.classList.remove('active');
                } else {
                    blockHeader.classList.add('active');
                    blockContents.classList.add('active');

                    // Remove active from all other blocks.
                    let stockPopup = document.getElementById("StoreStock");
                    const allBlockHeaders = stockPopup.querySelectorAll(".blockHeader");
                    allBlockHeaders.forEach((otherBlockHeader) => {
                        // But not the one we just opened.
                        if (otherBlockHeader !== blockHeader) {
                            const otherBlockContents = otherBlockHeader.nextElementSibling as HTMLElement;

                            otherBlockHeader.classList.remove('active');
                            otherBlockContents.classList.remove('active');
                        }
                    });
                }
            }
        });
    }

    private WireUpImagePaging() {
        document.getElementById("mobile-previous-image")?.addEventListener("click", (e) => this.onMobileImagePreviousClicked(e));
        document.getElementById("mobile-next-image")?.addEventListener("click", (e) => this.onMobileImageNextClicked(e));
        document.getElementById("c-previous-image")?.addEventListener("click", (e) => this.onDesktopImagePreviousClicked(e));
        document.getElementById("c-next-image")?.addEventListener("click", (e) => this.onDesktopImageNextClicked(e));
    }

    private onMobileImagePreviousClicked(e: MouseEvent): void {
        e.preventDefault();
        this.pageImage(-1, "mobilethumbList", this.mobileZoomId, this.mobileVideoId);
    }

    private onMobileImageNextClicked(e: MouseEvent): void {
        e.preventDefault();
        this.pageImage(+1, "mobilethumbList", this.mobileZoomId, this.mobileVideoId);
    }

    private onDesktopImagePreviousClicked(e: MouseEvent): void {
        e.preventDefault();
        let newSlide = this.pageImage(-1, "thumbList", this.desktopZoom1Id, this.desktopVideoId);
        if (newSlide) {
            this.triggerSlideTwoUpdate(newSlide);
        }
    }

    private onDesktopImageNextClicked(e: MouseEvent): void {
        e.preventDefault();
        let newSlide = this.pageImage(+1, "thumbList", this.desktopZoom1Id, this.desktopVideoId);
        if (newSlide) {
            this.triggerSlideTwoUpdate(newSlide);
        }
    }

    private triggerSlideTwoUpdate(slideLink: HTMLAnchorElement): void {
        let slideInfo = this.slideInfoFromLink(slideLink);

        if (slideInfo.isVideo) {
            // We've just loaded a video into the left pane. When we load an image via MagicScroll an event is
            // triggered which then updates the second pane. We have to trigger that event manually here.
            this.SetCurrentThumbIndex(this.desktopZoom1Id, slideLink);
        }
    }

    private pageImage(slideIncrement: number, thumblistId: string, imageZoomId: string, videoDivId: string): HTMLAnchorElement {
        let selectedThumb = document.querySelector<HTMLAnchorElement>(`div#${thumblistId} .${this.magicZoomThumbSelectedClass}`);

        if (!selectedThumb) {
            return;
        }

        let slideInfo = this.slideInfoFromLink(selectedThumb);

        let newSlideIndex = slideInfo.index + slideIncrement;
        if (newSlideIndex >= this.slideCount()) {
            newSlideIndex = 0;
        }
        else if (newSlideIndex < 0) {
            newSlideIndex = this.slideCount() - 1;
        }

        let newSlide = <HTMLAnchorElement>document.querySelectorAll(`div#${thumblistId} a`)[newSlideIndex];

        // Clear mz-thumb-selected and reapply to the new active anchor.
        selectedThumb.classList.remove(this.magicZoomThumbSelectedClass);
        newSlide.classList.add(this.magicZoomThumbSelectedClass);

        // Show / hide mobile image / video pane as required.
        this.setPaneVisibility(newSlide, imageZoomId, videoDivId);

        // Because we are not using MagicScroll for the thumbs (hidden on mobile) we need to set the zoom image ourselves.
        if (!slideInfo.isVideo) {
            // Switch to the new slide using the element (not the slide index) as MagicZoom gets the index wrong as it does not count the videos.
            MagicZoom.switchTo(imageZoomId, newSlide);
        }

        return newSlide;
    }

    public SetButtonState(stockLevel: StockLevel): void {
        this.setStockAvailableText("Select size and colour above to view live stock availability");

        let iconSpan = this.addToBasketButton.querySelector("span");

        // Find the last element in the button which is a text node.
        let buttonTextNode = this.addToBasketButton.childNodes[this.addToBasketButton.childNodes.length - 1];

        let stockAvailable = false;

        this.addToBasketButton.classList.remove("outofstock");
        this.addToBasketButton.classList.remove("notice");
        this.addToBasketButton.classList.remove("off");
        this.addToBasketButton.classList.remove("on");

        if (StockLevel.Stock_Unavailable === stockLevel || StockLevel.Stock_Enquiries === stockLevel) {
            this.addToBasketButton.classList.add("outofstock");
            this.addToBasketButton.classList.add("notice");
            iconSpan.className = "";
            buttonTextNode.nodeValue = 'Email me when in stock';
        }
        else if (StockLevel.Stock_LowStock == stockLevel || StockLevel.Stock_InStock == stockLevel) {
            this.addToBasketButton.classList.add("on");
            stockAvailable = true;
            iconSpan.className = "icons icon-bag";
            buttonTextNode.nodeValue = 'Add to Basket';
        }
        else {
            this.addToBasketButton.classList.add("off");
            iconSpan.className = "icons icon-bag";
            buttonTextNode.nodeValue = 'Add to Basket';
        }

        if (this.checkStoreStockElement) {
            let checkStoreStockContainer = this.checkStoreStockElement.parentElement;

            if (stockAvailable) {
                checkStoreStockContainer.classList.add("available");
            }
            else {
                checkStoreStockContainer.classList.remove("available");
            }
        }
    }

    private onStoreStockCheckClicked(e: MouseEvent): void {
        e.preventDefault();

        let selectedProduct = this.StockInfoForCurrentSizeAndColour();

        if (!selectedProduct || StockLevel.Stock_NotSelected === selectedProduct.availability) {
            alert('You need to select a size and colour combination first.');
            return;
        }

        document.getElementById("check-store-stock").classList.add("working");

        let url = `${this.localeUrlPrefix}/ws/store-stock/${selectedProduct.productId}`;
        fetch(url, { method: "POST" })
            .then(response => response.text())
            .then(html => this.StoreStockCheckSuccess(html))
            .catch(() => this.StoreStockCheckFailed());
    }

    private StoreStockCheckSuccess(html: string): void {
        document.getElementById("check-store-stock").classList.remove("working");

        let contentElement = document.getElementById("store-stock-content");
        contentElement.innerHTML = html;

        let storeModal = <HTMLDivElement>document.getElementById("store-stock-popup");
        window.Modal.OpenModal(storeModal, () => { });
    }

    private StoreStockCheckFailed(): void {
        document.getElementById("check-store-stock").classList.remove("working");

        let contentElement = document.getElementById("store-stock-content");
        let html = "<div id='StoreStock'>"
            + "<h2>Sorry, we could not check the store stock levels at the moment.</h2>"
            + "<p class='bottom'>Please contact the store directly to reserve stock.</p>"
            + "<a href='#' id='StoreStockClose'><span class='close'></span></a>"
            + "</div>";
        contentElement.innerHTML = html;

        let storeModal = <HTMLDivElement>document.getElementById("store-stock-popup");
        window.Modal.OpenModal(storeModal, () => { });
    }

    private onAddToBasketClicked(e: MouseEvent): void {
        e.preventDefault();

        let price: number = null;
        let selectedProduct = this.StockInfoForCurrentSizeAndColour();

        if (!selectedProduct || StockLevel.Stock_NotSelected === selectedProduct.availability) {
            alert('You need to select a size and colour combination first.');
            return;
        }
        else if (StockLevel.Stock_Unavailable === selectedProduct.availability || StockLevel.Stock_Enquiries === selectedProduct.availability) {
            window.location.href = "/out-of-stock?ProductId=" + selectedProduct.productId;
            return;
        }

        if (this.productClass === ProductClass.GiftCard) {
            price = this.basketCommon.giftCardValue();

            if (price === null) {
                alert('Please enter a gift card value.');
                return;
            }
        }
        else {
            price = selectedProduct.priceGbp;
        }

        this.basketCommon.AddToBasket(selectedProduct.productId, price);
    }

    // Because the MagicX js libs don't handle videos we need to calculate the thumbnail index minus
    // any previous videos when passing the current slide to show.
    private countOfVideosBeforeSlide(activeIndex: number): number {
        let videoCount = 0;

        let elements = document.getElementsByClassName("zoom-1-thumb");
        for (const element of elements) {
            let slideLink = <HTMLAnchorElement>element;
            let slideInfo = this.slideInfoFromLink(slideLink);

            if (slideInfo.index < activeIndex) {
                if (slideInfo.isVideo) {
                    videoCount++;
                }
            }
        }

        return videoCount;
    }

    private findLowestSlideLink(slideLinks: HTMLCollectionOf<HTMLAnchorElement>): HTMLAnchorElement {
        let lowest: HTMLAnchorElement;
        let lowestIndex = 999;

        for (const slideLink of slideLinks) {
            let slideInfo = this.slideInfoFromLink(slideLink);

            if (slideInfo.index < lowestIndex) {
                lowest = slideLink;
                lowestIndex = slideInfo.index;
            }
        }

        if (!lowest) {
            lowest = <HTMLAnchorElement>slideLinks[0];
        }

        return lowest;
    }

    private slideInfoFromLink(slideLink: HTMLAnchorElement): ISlideCaptionAndIndex {
        let isVideo = slideLink.dataset["isVideo"];

        return {
            caption: slideLink.dataset["colour"],
            index: parseInt(slideLink.dataset["imageIndex"]),
            isNewColour: (slideLink.dataset["isNewColour"] && slideLink.dataset["isNewColour"].toLowerCase() === "true"),
            isVideo: (isVideo && isVideo.toLowerCase() === "true")
        };
    }

    public FindAndClickThumbnail(colourId: number): void {
        let imgLinks = document.getElementsByClassName("c-colour-image-" + colourId) as HTMLCollectionOf<HTMLAnchorElement>;

        if (imgLinks.length > 0) {
            // Find the lowest slideIndex as they are in priority order, but MagicScroll moves them around in the DOM.
            let slideLink = this.findLowestSlideLink(imgLinks);
            let slideInfo = this.slideInfoFromLink(slideLink);

            slideInfo.index -= this.countOfVideosBeforeSlide(slideInfo.index);

            if (this.currentThumbnailIndex !== slideInfo.index) {
                MagicZoom.switchTo(this.desktopZoom1Id, slideInfo.index);
                MagicZoom.switchTo(this.mobileZoomId, slideInfo.index);
                this.currentThumbnailIndex = slideInfo.index;
            }

            // When we call MagicZoom.switchTo above, it selects the correct thumb but does not move the scroll which is why we do it here. 
            let thumbListElement = document.getElementById("thumbList");
            if (this.isVisible(thumbListElement)) {
                if (slideInfo.index + 2 <= this.slideCount()) { // The thumbs have 5 items, this puts the selected one at the top instead of the middle.
                    slideInfo.index += 2;
                }
                MagicScroll.jump('thumbList', slideInfo.index);
            }

            this.updateZoomCaption("1", slideInfo.caption);
            this.updateZoomCaption("3", slideInfo.caption);
            this.updateIsNewColourFlash(slideInfo.isNewColour);

            this.setPaneVisibility(slideLink, this.desktopZoom1Id, this.desktopVideoId);
        }
    }

    private updateIsNewColourFlash(isNewColour: boolean): void {
        // Find all li elements with a class of 'new-colour' that are contained within the product hints ul.
        const newFlashes = document.querySelectorAll("ul.product-flag li.new-colour");

        newFlashes.forEach((flash) => {
            if (isNewColour) {
                flash.classList.remove("hidden");
            }
            else {
                flash.classList.add("hidden");
            }
        });
    }

    private isVisible(el: HTMLElement): boolean {
        let style = window.getComputedStyle(el);
        return style.display !== 'none';
    }

    private slideCount(): number {
        // Extract the number of slides from the data attribute of the slide container. More reliable than asking MagicScroll.
        let tl = document.getElementById("thumbList");
        let slideCount = parseInt(tl.dataset["imageCount"]);

        return slideCount;
    }

    public updateZoomWindows(): void {
        let imageList = <NodeListOf<HTMLAnchorElement>>document.querySelectorAll("div.mcs-item>a");

        if (imageList.length === 0) {
            return; // Don't run on mobile as no magic scroll loaded.
        }

        let imageCount = this.slideCount();

        if (imageCount <= 1) {
            // Not enough images, second one is a placeholder that we should leave alone.
            return;
        }

        // Ensure the slide index is within the image array bounds.
        if (this.currentThumbnailIndex < 0) {
            this.currentThumbnailIndex = imageCount - 1;
        }
        else if (this.currentThumbnailIndex >= imageCount) {
            this.currentThumbnailIndex = 0;
        }

        let firstImageAnchorElement = this.findImageInList(imageList, this.currentThumbnailIndex);
        let secondImageIndex = this.currentThumbnailIndex + 1;

        // If the left hand image is the last in the list, wrap around to the start.
        if (secondImageIndex > imageCount - 1) {
            secondImageIndex = 0;
        }

        let firstImageColourDescription = firstImageAnchorElement.dataset["colour"];
        this.updateZoomCaption("1", firstImageColourDescription);

        // Find the second thumbnail by index.
        let secondImageAnchorElement = this.findImageInList(imageList, secondImageIndex);
        let secondImageColourDescription = secondImageAnchorElement.dataset["colour"];
        let secondImageMediumUrl = secondImageAnchorElement.dataset["image"];
        let secondImageThumbElement = <HTMLImageElement>secondImageAnchorElement.firstChild;
        let secondMagicZoomAnchor = <HTMLAnchorElement>document.getElementById(this.desktopZoom2Id);
        let secondMagicZoomImage = <HTMLImageElement>secondMagicZoomAnchor.firstChild;

        //**************************
        let isVideo = secondImageAnchorElement.dataset["isVideo"];
        let videoDiv = document.getElementById("video-right");

        if (isVideo && isVideo.toLowerCase() === "true") {
            let mediaUrl = secondImageAnchorElement.dataset["image"];
            this.recreateVideo(videoDiv, mediaUrl);

            secondMagicZoomAnchor.classList.add('hidden');
            videoDiv.classList.remove('hidden');
        }
        else {
            MagicZoom.update(this.desktopZoom2Id, secondImageAnchorElement.href, secondImageMediumUrl);

            secondMagicZoomAnchor.classList.remove('hidden');
            videoDiv.classList.add('hidden');
        }
        //**************************

        this.updateZoomCaption("2", secondImageColourDescription);
        secondMagicZoomAnchor.title = secondImageAnchorElement.title;
        secondMagicZoomImage.alt = secondImageThumbElement.alt;

        // MagicZoom won't let us update the title tags for this.desktopZoom2Id so we update it in the DOM directly.
        let zoomWindowTitles = document.getElementsByClassName("mz-caption"); // Zoom window captions created by MagicZoom.
        for (var i = 0; i < zoomWindowTitles.length; i++) {
            let zoomWindowTitle = <HTMLDivElement>zoomWindowTitles[i];

            // Only update the title text for elements that are not the first zoom (we have 3 on the page).
            if (zoomWindowTitle.innerText !== firstImageAnchorElement.title) {
                zoomWindowTitle.innerText = secondMagicZoomAnchor.title;
            }
        }
    }

    private findImageInList(imageList: NodeListOf<HTMLAnchorElement>, slideIndex: number): HTMLAnchorElement {

        for (var i = 0; i < imageList.length; i++) {
            let thumbSlideInfo = this.slideInfoFromLink(imageList[i]);

            if (thumbSlideInfo.index === slideIndex) {
                return imageList[i];
            }
        }

        return imageList[0];
    }

    private onAddToWishlistClicked(e: MouseEvent): boolean {
        e.preventDefault();

        let selectedProduct = this.StockInfoForCurrentSizeAndColour();

        if (!selectedProduct || StockLevel.Stock_NotSelected === selectedProduct.availability) {
            alert('Please select a size and colour first in order to add to your wish list.');
            return false;
        }

        // Call the web service to add the item which will then ping back and call the success / fail function.
        this.quickAdd.addToWishList(selectedProduct.productId, this.addToWishlistButton);

        return false;
    }

    private UpdateDelivery(productId: number, silentUpdate: boolean): void {
        if (productId === 0) {
            this.SetDeliveryText('Select the size and colour (above) to view currently available delivery options for this product.', false);
            return;
        }
        if (!silentUpdate) {
            this.SetDeliveryText('Calculating delivery, please wait.', false);
        }

        const urlStub = this.urlStubFromPath();

        let postData = {
            productId: productId,
            urlStub: urlStub,
        };

        let url = this.localeUrlPrefix + "/ws/delivery";
        fetch(url,
            {
                body: JSON.stringify(postData),
                headers: {
                    "Content-Type": "application/json"
                },
                method: "POST",
            })
            .then(response => response.json())
            .then(json => this.UpdateDeliverySuccess(json))
            .catch(() => this.UpdateDeliveryFailed());
    }

    private urlStubFromPath(): string {
        // 1. Get the path (e.g. "/de/url-stub" or "/de/url-stub.html").
        const path = window.location.pathname;

        // 2. Split on "/" and remove empty segments
        const segments = path.split("/").filter(Boolean); // e.g. ["de", "url-stub"].

        // 3. Get the last segment (this is the "stub" part, which might have an extension).
        let lastSegment = segments[segments.length - 1] || "";

        // 4. If there's a dot, remove everything from the dot to the end.
        const dotIndex = lastSegment.indexOf(".");

        if (dotIndex !== -1) {
            lastSegment = lastSegment.slice(0, dotIndex);
        }

        return lastSegment;
    }

    private SetDeliveryText(text, success) {
        document.getElementById("DeliveryMessage").textContent = text;
        let deliveryTable = document.getElementById("DeliveryTable");

        if (success) {
            deliveryTable.classList.remove("hidden");
        }
        else {
            deliveryTable.classList.add("hidden");
        }
    }

    private UpdateDeliverySuccess(shippingOptions: IShippingOption[]) {
        if (shippingOptions === null || shippingOptions.length !== 3) {
            this.SetDeliveryText("A delivery cost could not be calculated.", false);
            this.hideExpressDeliveryTime();
            return;
        }

        if (this.UpdateShippingUiRow("ShipExpressRow", "ShipExpressChargeLabel", shippingOptions[0])) {
            this.showExpressDeliveryTime(shippingOptions[0]);
        }
        else {
            this.hideExpressDeliveryTime();
        }
        this.UpdateShippingUiRow("ShipStandardRow", "ShipStandardChargeLabel", shippingOptions[1]);
        let economyAvailable = this.UpdateShippingUiRow("ShipEconomyRow", "ShipEconomyChargeLabel", shippingOptions[2]);

        let showFreeDeliveryText = false;
        let selectedProduct = this.StockInfoForCurrentSizeAndColour();
        if (selectedProduct && selectedProduct.priceGbp >= 45) {
            // Hide if not available or not free.
            if (!economyAvailable || (shippingOptions[2].formattedCharge != null && shippingOptions[2].formattedCharge.indexOf("Free") === -1)) {
                showFreeDeliveryText = false;
            }
            else {
                showFreeDeliveryText = true;
            }
        }
        let freeDeliveryContainer = document.querySelector(".delivery-text-container");
        let deliveryTextPlaceHolder = document.querySelector(".delivery-text-placeholder");
        if (showFreeDeliveryText) {
            freeDeliveryContainer.classList.remove("hidden");
            deliveryTextPlaceHolder.classList.add("hidden");
        }
        else {
            freeDeliveryContainer.classList.add("hidden");
            deliveryTextPlaceHolder.classList.remove("hidden");
        }

        this.SetDeliveryText("The delivery options for all selected items are shown below. Default delivery pricing and timescale are based on delivery to UK mainland destinations, alternative destinations can be selected on the basket page or by logging into your account.", true);
        this.setDeliveryUpdateTimer(60, () => {
            if (selectedProduct) {
                this.UpdateDelivery(selectedProduct.productId, false);
            }
        });
    }

    private UpdateShippingUiRow(tableRowId: string, labelId: string, shipOption: IShippingOption) {
        let tableRowRef = document.getElementById(tableRowId);
        let labelRef = document.getElementById(labelId);

        if (shipOption.formattedCharge == null || shipOption.formattedCharge == "") {
            tableRowRef.classList.add("hidden");
            labelRef.classList.add("hidden");
            return false; // Indicate row is not visible.
        }
        tableRowRef.classList.remove("hidden");
        labelRef.classList.remove("hidden");
        labelRef.textContent = shipOption.formattedCharge;
        let countryDescription = tableRowRef.querySelector(".country-description");
        if (countryDescription) {
            countryDescription.innerHTML = shipOption.countrySpecificDescription;
        }
        return true; // Indicate row is visible.
    }

    private UpdateDeliveryFailed() {
        this.SetDeliveryText('Sorry, we could not calculate delivery as an error occurred.', false);
        this.stopDeliveryUpdateTimer();
        this.hideExpressDeliveryTime();
    }

    private UpdateLinked(productId: number, styleId: number, colourId: number): void {
        this.ClearLinkedProductsTab();

        if (productId === 0 || isNaN(colourId)) {
            return;
        }
        this.setLinkedProductsText("Checking for compatible products, please wait...");

        this.lastRequestTime = Date.now();

        let postData = {
            productId: productId,
            styleId: styleId,
            colourId: colourId,
            lastRequestTime: this.lastRequestTime
        };

        let url = this.localeUrlPrefix + "/ws/linked";

        fetch(url,
            {
                body: JSON.stringify(postData),
                headers: {
                    "Content-Type": "application/json"
                },
                method: "POST",
            })
            .then(response => response.json())
            .then(json => this.UpdateLinkedSuccess(json))
            .catch(() => this.UpdateLinkedFailed());
    }

    private setLinkedProductsText(text: string): void {
        let lpm = document.getElementById("LinkedProductsMessage");

        if (lpm) {
            lpm.textContent = text;
        }
    }

    private UpdateLinkedSuccess(recommendations: IProductRecommendations): void {

        if (this.lastRequestTime !== recommendations.lastRequestTime) {
            // Too many requests at once, only load the last one.
            return;
        }

        if (recommendations === null || recommendations.compatibles.length === 0) {
            this.setLinkedProductsText("We don't have any in stock compatible items for this product.");
        }
        else {
            let compatibleProductsTable = document.getElementById("LinkedProductsTable");

            for (const product of recommendations.compatibles) {
                let html = this.CreateCompatibleProductHtml(product);
                compatibleProductsTable.insertAdjacentElement("beforeend", html);
            }

            compatibleProductsTable.classList.remove("hidden");
            this.setLinkedProductsText("Based on the selected item, these are the available accessories:");
        }

        this.loadTheLook(recommendations.theLook, this.desktopTheLookContainerId, this.desktopTheLookMagicScrollId);
        this.loadTheLook(recommendations.theLook, this.mobileTheLookContainerId, this.mobileTheLookMagicScrollId);
    }

    private loadTheLook(products: ITheLookLinkModel[], containerId: string, magicScrollId: string) {
        MagicScroll.stop(magicScrollId);

        let theLookProducts = this.ClearTheLookRecommendations(containerId, magicScrollId);

        if (!theLookProducts) {
            return;
        }

        if (products && products.length >= 2) {
            for (const product of products) {
                theLookProducts.append(this.CreateTheLookProductHtml(product));
            }
        }
        else {
            // No products, show the cached version of recommendations instead.
            window.scrollProducts?.restoreTheLookCache(containerId, magicScrollId);
        }

        MagicScroll.refresh(magicScrollId);
        MagicScroll.start(magicScrollId);
        this.setTheLookVisibility(true, containerId);
    }

    private setTheLookVisibility(visible: boolean, containerId: string): void {
        let theLookContainer = document.getElementById(containerId);

        if (theLookContainer) {
            if (visible) {
                theLookContainer.classList.remove("hidden");
            }
            else {
                theLookContainer.classList.add("hidden");
            }
        }
    }

    private CreateCompatibleProductHtml(product: ILinkedProductModel): HTMLElement {
        let containingTableRow = document.createElement("tr");
        containingTableRow.className = "related";

        let containingTableCell = document.createElement("td");
        containingTableRow.appendChild(containingTableCell);

        let titleDiv = document.createElement("div");
        titleDiv.className = "title-box";

        let productImg = document.createElement("img");
        productImg.src = product.imageUrl;
        productImg.height = 90;
        productImg.width = 90;
        titleDiv.appendChild(productImg);

        let nameContainer = document.createElement("span");
        nameContainer.className = "name";

        let productLink = document.createElement("a");
        productLink.href = product.styleUrl;
        productLink.innerText = product.name;
        nameContainer.appendChild(productLink);

        let productDescription = document.createElement("p");
        productDescription.className = "bottom description";
        productDescription.innerText = product.description;

        titleDiv.appendChild(nameContainer);
        titleDiv.appendChild(productDescription);

        let controlDiv = document.createElement("div");
        controlDiv.className = "control-box";

        this.appendHtml(controlDiv, product.formattedPrice);

        let basketLink = document.createElement("a");
        basketLink.className = "btn";
        basketLink.onclick = () => this.basketCommon.AddToBasket(product.productId, null);
        basketLink.innerText = "Add to Basket";
        controlDiv.appendChild(basketLink);

        containingTableCell.appendChild(titleDiv);
        containingTableCell.appendChild(controlDiv);

        return containingTableRow;
    }

    private appendHtml(el, str) {
        var div = document.createElement("div");
        div.innerHTML = str;
        while (div.children.length > 0) {
            el.appendChild(div.children[0]);
        }
    }

    private CreateTheLookProductHtml(product: ITheLookLinkModel): HTMLElement {
        var containingDiv = document.createElement("div");
        containingDiv.className = "item";

        var productLink = document.createElement("a");
        productLink.href = product.styleUrl;
        productLink.className = "image";
        containingDiv.appendChild(productLink);

        var productImg = document.createElement("img");
        productImg.src = product.imageUrl;
        productLink.appendChild(productImg);

        var productTitle = document.createElement("a");
        productTitle.href = product.styleUrl;
        productTitle.className = "name";
        productTitle.innerText = product.name;
        containingDiv.appendChild(productTitle);

        var priceContainer = document.createElement("p");
        priceContainer.className = "price";
        containingDiv.appendChild(priceContainer);

        var productPrice = document.createElement("span");
        productPrice.className = "new-price";
        productPrice.innerHTML = product.formattedPrice;
        priceContainer.appendChild(productPrice);

        var basketLink = document.createElement("a");
        basketLink.className = "looksAddToBasket btn";
        basketLink.onclick = () => this.quickAdd.showTheLook(product.styleId);
        basketLink.innerText = "Add to Basket";
        containingDiv.appendChild(basketLink);

        return containingDiv;
    }

    private UpdateLinkedFailed() {
        this.setLinkedProductsText("Sorry, we could not find any compatible products.");
    }

    private ClearTheLookRecommendations(containerId: string, magicScrollId: string): HTMLElement {
        this.setTheLookVisibility(false, containerId);

        let theLooksList = document.getElementById(magicScrollId);

        if (theLooksList) {
            theLooksList.innerHTML = "";
        }

        return theLooksList;
    }

    private ClearLinkedProductsTab() {
        this.setLinkedProductsText("Select the product size and colour (above) to view current accessories.");

        let linkedProductsTable = document.getElementById("LinkedProductsTable");

        if (linkedProductsTable) {
            linkedProductsTable.innerHTML = "";
            linkedProductsTable.classList.add("hidden");
        }
    }

    public ProductSelectionChanged(selectedProduct: ISelectedSizeAndColour): void {
        let available = false;

        if (selectedProduct !== null) {
            let stock = this.StockInfoForSizeAndColour(selectedProduct.colourId, selectedProduct.size);

            if (stock) {
                this.SetButtonState(stock.availability);

                this.setStockAvailableText(stock.stockLevelMessage);
                // Remember the productId for postback and add to basket / wish list.
                this.selectedProductId = stock.productId;

                this.updateUiPrices(stock, selectedProduct.colourId);

                available = true;
            }
            else if (selectedProduct.colourId) {
                let colourStock = this.StockInfoForColour(selectedProduct.colourId);

                if (colourStock) {
                    this.updateUiPrices(colourStock, selectedProduct.colourId);
                }

                //ScarabQueue.push(['view', colourStock.code.substring(0, 10) + "-" + this.styleId.toString()]);
            }
        }
        if (!available) {
            this.SetButtonState(StockLevel.Stock_NotSelected);
            this.ClearLinkedProductsTab();
            this.setStockAvailableText("");
            this.selectedProductId = 0;
        }
    }

    private updateUiPrices(stock: IProductPageStockModel, colourId: number): void {
        // Fire off a call to the web service to calculate how much the delivery options will be if this item is added.
        this.UpdateDelivery(stock.productId, false);
        this.UpdateLinked(stock.productId, this.styleId, colourId);

        this.FindAndClickThumbnail(colourId);

        let percentageOffHtml = '';

        if (stock.percentageSaving !== null) {
            percentageOffHtml = stock.percentageSaving;
        }

        if (this.priceExVatElement) {
            this.priceExVatElement.innerHTML = stock.priceExVat;
        }

        if (this.priceElement) {
            this.priceElement.innerHTML = stock.priceHeading;
        }

        // Percentage off is only output on physical products so will be missing for gift cards.
        if (this.percentageOffElement) {
            this.percentageOffElement.innerHTML = percentageOffHtml;

            if (percentageOffHtml) {
                this.percentageOffElement.classList.remove('hidden');
            } else {
                this.percentageOffElement.classList.add('hidden');
            }
        }
    }

    private endsWith(text, suffix): boolean {
        return text.indexOf(suffix, text.length - suffix.length) !== -1;
    };

    public StockInfoForColour(colourId: number): IProductPageStockModel {

        for (let key in stockInfo) {
            if (this.endsWith(key, colourId.toString())) {
                return stockInfo[key];
            }
        }

        return null;
    }

    public StockInfoForSizeAndColour(colourId: number, size: string): IProductPageStockModel {
        if (this.productClass === ProductClass.GiftCard) {
            // Product is a gift card which does not have a size list, return the first stock item.
            return this.firstStockItem();
        }

        if (isNaN(colourId) || !size) {
            return null;
        }

        return stockInfo[this.keyForStockLookup(colourId, size)];
    }

    private firstStockItem(): IProductPageStockModel {
        let posCode = Object.keys(stockInfo)[0];
        return stockInfo[posCode];
    }

    private StockInfoForCurrentSizeAndColour(): IProductPageStockModel {
        if (this.productClass === ProductClass.GiftCard) {
            // Product is a gift card which does not have a size list, return the first stock item.
            return this.firstStockItem();
        }

        let selected = this.selectorUi.SelectedSizeAndColour();
        if (selected === null) {
            return null;
        }

        return this.StockInfoForSizeAndColour(selected.colourId, selected.size);
    }

    private keyForStockLookup(colourId: number, sizeCode: string): string {
        return sizeCode.toLowerCase() + '-' + colourId;
    }

    private get selectedProductId(): number {
        let el = document.getElementById("SelectedProductId") as HTMLInputElement;

        if (!el) {
            return 0;
        }

        let selectedProductId = parseInt(el.value);
        return selectedProductId;
    }

    private set selectedProductId(val: number) {
        let el = document.getElementById("SelectedProductId") as HTMLInputElement;

        if (el) {
            el.value = val.toString();
        }
    }

    private showExpressDeliveryTime(shipOption: IShippingOption): void {
        if (!shipOption.deliveryCutOff || !shipOption.nextDeliverySlot) {
            this.hideExpressDeliveryTime();
            return;
        }
        let container = this.findDeliveryContainer();

        let textOrderWithin = document.createTextNode('Order within ');
        let spanTimescale = document.createElement('span');
        spanTimescale.innerText = shipOption.deliveryCutOff;
        let initalTextForDelivery = document.createTextNode(' for UK delivery on ');
        let spanDate = document.createElement('span');
        spanDate.innerText = shipOption.nextDeliverySlot;
        let finalTextForDelivery = document.createTextNode(' by selecting Express delivery');

        this.clearElementChildren(container);
        container.appendChild(textOrderWithin);
        container.appendChild(spanTimescale);
        container.appendChild(initalTextForDelivery);
        container.appendChild(spanDate);
        container.appendChild(finalTextForDelivery);
    }

    private hideExpressDeliveryTime(): void {
        let anchor = document.createElement('a');

        //anchor.className = "left promo-text" ;
        anchor.href = "/returns-policy";
        anchor.innerText = "90 day satisfaction guarantee with free UK returns and excellent customer service";

        let container = this.findDeliveryContainer();
        this.clearElementChildren(container);
        container.appendChild(anchor);
    }

    private clearElementChildren(node): void {
        while (node.hasChildNodes()) {
            node.removeChild(node.lastChild);
        }
    }

    private findDeliveryContainer(): HTMLDivElement {
        return <HTMLDivElement>(document.getElementsByClassName('express-delivery')[0]);
    }

    private setDeliveryUpdateTimer(seconds, func): void {
        this.stopDeliveryUpdateTimer();
        if (seconds > 0) {
            this.deliveryTimerHander = setTimeout(func, seconds * 1000);
        }
    }

    private stopDeliveryUpdateTimer(): void {
        if (this.deliveryTimerHander != 0) {
            clearTimeout(this.deliveryTimerHander);
        }
        this.deliveryTimerHander = 0;
    }

    private updateZoomCaption(captionId: string, text: string) {
        let element = document.getElementById("zoomcaption-" + captionId);

        if (element) {
            element.innerText = text;
        }
    }

    private setStockAvailableText(text: string) {
        let stockAvailableTextElement = document.getElementById("stockAvailableMessage") as HTMLSpanElement;

        if (stockAvailableTextElement) {
            stockAvailableTextElement.textContent = text;
        }
    }
}

if (document.body.dataset.pageInit == "product") {
    Product.Init();
}
